// Google Drive 双方向同期。学習データ（単語/SRS/再生位置/ブックマーク）を
// ユーザー自身の Drive（Immerso フォルダ）で双方向同期する（旧 R2 版を置き換え）。
//  - vocab: vocab.json を tombstone 付きでマージ（PC syncVocab と同形）
//  - 再生状態: 項目の <name>.bundle.json 内の state.json を更新し catalog の ver を上げる
//    （PC は state.json をバンドルの一部として fp/ver で同期するため、ver を上げれば PC が pull する）
import { db } from './db.js'
import * as gdrive from './gdrive.js'
import { listDoc, mergeRemoteDoc } from './vocab.js'
import { localFolders, foldersUpdatedAt, replaceFoldersFromSync } from './library.js'
import { can } from './entitlements.js'

const VOCAB_KEY = 'vocab.json'

// フォルダ構成の同期（whole-doc LWW）。folders.json の updatedAt が新しい方を採用＝PCと同方式。
// 連携時のみ。リモートが新しければ置き換え、ローカルが新しければ push。
export async function syncFolders() {
  if (!gdrive.isConnected()) return { ok: true, skipped: true }
  let remote = { folders: [], updatedAt: null }
  try {
    remote = await gdrive.fetchFoldersDoc()
  } catch (e) {
    return { ok: false, error: e.message || String(e) }
  }
  const localUA = await foldersUpdatedAt()
  const remoteUA = remote.updatedAt || ''
  if (remoteUA && (!localUA || remoteUA > localUA)) {
    await replaceFoldersFromSync(remote.folders, remoteUA) // リモートが新しい→置き換え
    return { ok: true, pulled: true }
  }
  if (localUA && localUA > remoteUA) {
    await gdrive.putJSON('folders.json', { folders: await localFolders(), updatedAt: localUA }) // ローカルが新しい→push
    return { ok: true, pushed: true }
  }
  return { ok: true }
}

// 保存単語の同期: リモート取得 → マージ → 全体を書き戻し。
async function syncVocab() {
  let remote = {}
  try {
    remote = await gdrive.getJSON(VOCAB_KEY)
  } catch (e) {
    if (e.name !== 'NotFound') throw e // 初回（未作成）は空で続行
  }
  const stats = await mergeRemoteDoc(remote)
  await gdrive.putJSON(VOCAB_KEY, await listDoc())
  return stats
}

// バンドル（字幕/トラック/状態/メタ）をローカルへ反映（メディアは触らない）。pull で使う。
async function applyBundle(d, hash, entry, bundle) {
  const files = bundle.files || {}
  const metaContent = bundle.metaContent || {}
  const tx = d.transaction(['tracks', 'state'], 'readwrite')
  // 既存 track-* を消してリモートに合わせる（古い候補を残さない）。
  let cur = await tx.objectStore('tracks').openCursor()
  while (cur) {
    if (String(cur.key).startsWith(hash + '/')) cur.delete()
    cur = await cur.continue()
  }
  for (const name of Object.keys(files)) {
    if (name === 'state.json') {
      try {
        tx.objectStore('state').put({ ...JSON.parse(files[name]), dirty: false }, hash)
      } catch {
        /* noop */
      }
    } else {
      tx.objectStore('tracks').put(files[name], `${hash}/${name}`)
    }
  }
  await tx.done
  const it = await d.get('items', hash)
  if (it) {
    it.metaContent = metaContent
    it.name = entry.name || metaContent.name || it.name
    it.duration = entry.duration || metaContent.duration || it.duration
    it.hasSubtitles = !!entry.hasSubtitles
    it.fileNames = Object.keys(files)
    await d.put('items', it)
  }
}

// 再生状態の同期。ローカルが dirty なら push（ver+1）、リモートが新しければ pull（リモート優先）。
// Drive ではバンドルは catalog の bundleFile（人間可読名）で参照する。
async function syncState(catalog) {
  const d = await db()
  const items = await d.getAll('items')
  let pushed = 0
  let pulled = 0
  const changedEntries = {} // この同期で ver を上げた catalog 項目（最後に最新へマージして書く）

  for (const it of items) {
    const hash = it.hash
    const entry = catalog.items[hash]
    if (!entry || entry.deleted || !entry.bundleFile) continue
    const remoteVer = entry.ver || 0
    const state = await d.get('state', hash)
    const dirty = !!(state && state.dirty)

    // 初回（syncVer 未設定）: DL 済み＝現行とみなして基準を合わせる。
    if (it.syncVer == null) {
      it.syncVer = remoteVer
      await d.put('items', it)
    }
    const localVer = it.syncVer

    if (remoteVer > localVer) {
      // リモートが新しい → pull（ローカルの未送信変更は破棄＝リモート優先）。
      const bundle = await gdrive.getJSON(entry.bundleFile)
      await applyBundle(d, hash, entry, bundle)
      it.syncVer = remoteVer
      await d.put('items', it)
      pulled++
    } else if (dirty) {
      // ローカルだけ変わった → リモートのバンドルに state を載せ替えて push、ver を上げる。
      const bundle = await gdrive.getJSON(entry.bundleFile)
      const files = bundle.files || {}
      files['state.json'] = JSON.stringify({
        pos: state.pos || 0,
        bookmarks: state.bookmarks || [],
        updatedAt: state.updatedAt || new Date().toISOString(),
      })
      await gdrive.putJSON(entry.bundleFile, {
        metaContent: bundle.metaContent || {},
        files,
      })
      const newVer = remoteVer + 1
      catalog.items[hash] = { ...entry, ver: newVer }
      changedEntries[hash] = catalog.items[hash]
      it.syncVer = newVer
      await d.put('items', it)
      await d.put('state', { ...state, dirty: false }, hash)
      pushed++
    }
  }

  // 多ライター安全マージ：最新の catalog に、今 ver を上げた項目だけを載せて書く（他端末の項目を消さない）。
  if (Object.keys(changedEntries).length) {
    await gdrive.mergeUpdateJSON('catalog.json', (cur) => {
      const items = (cur && cur.items) || {}
      for (const h of Object.keys(changedEntries)) items[h] = changedEntries[h]
      return { schemaVersion: (cur && cur.schemaVersion) || 1, items }
    })
  }
  return { pushed, pulled }
}

// バックグラウンド同期：重複起動を防ぎつつ syncAll を走らせる共通入口。
// 起動時・ライブラリ表示・復習完了後などから呼ぶ。失敗（オフライン等）は黙って握り潰す。
// 戻り値: syncAll の結果、または既に実行中なら null。
let _bgInflight = null
export function bgSync() {
  if (_bgInflight) return _bgInflight
  _bgInflight = (async () => {
    try {
      return await syncAll()
    } catch (e) {
      return { ok: false, error: e.message || String(e) }
    } finally {
      _bgInflight = null
    }
  })()
  return _bgInflight
}

// まとめて同期。戻り値 { ok, vocab?, state?, error? }。
// 権利で出し分け: vocab は sync.vocab、再生状態(メディア項目)は sync.audio が要る。
// 自動同期では Drive 未連携なら静かにスキップ（勝手にログイン画面を出さない）。
export async function syncAll() {
  const wantVocab = can('sync.vocab')
  const wantState = can('sync.audio')
  if (!wantVocab && !wantState) return { ok: true, skipped: true } // 同期権利なし
  if (!gdrive.isConnected()) return { ok: true, skipped: true, reason: 'drive-not-connected' }
  try {
    const vocab = wantVocab ? await syncVocab() : null
    let state = { pushed: 0, pulled: 0 }
    if (wantState) {
      let catalog = { items: {} }
      try {
        const c = await gdrive.getJSON('catalog.json')
        catalog = { items: c.items || {} }
      } catch (e) {
        if (e.name !== 'NotFound') throw e
      }
      state = await syncState(catalog)
    }
    return { ok: true, vocab, state }
  } catch (e) {
    return { ok: false, error: e.message || String(e) }
  }
}
